import type { NavigatorConfig, SDKStatus } from '../types/config';
import type { PageObservation } from '../types/observations';
import type { Action, ActionResult } from '../types/actions';
import type { HighlightOptions, HighlightInfo } from '../types/highlighter';
import type { SecurityViolation, SecurityConfig } from '../types/security';
import { EventBus } from './event-bus';
import { Logger, LogLevel } from './logger';
import { ConfigError } from '../utils/error-handler';
import type { EventHandler } from '../types/events';
import { WebSocketClient } from '../connection/websocket-client';
import { DOMObserver } from '../observer/dom-observer';
import { ActionExecutor } from '../actions/action-executor';
import { UIHighlighter } from '../highlighter/ui-highlighter';
import { SecurityManager } from '../security/security-manager';

export class EazyNavigator {
  private config: Required<NavigatorConfig>;
  private status: SDKStatus;
  private eventBus: EventBus;
  private logger: Logger;
  private wsClient: WebSocketClient;
  private domObserver: DOMObserver;
  private actionExecutor: ActionExecutor;
  private uiHighlighter: UIHighlighter;
  private securityManager: SecurityManager;

  constructor(config: NavigatorConfig) {
    // Validate required fields
    this.validateConfig(config);

    // Apply defaults for optional fields
    this.config = {
      mcpServerUrl: config.mcpServerUrl,
      apiKey: config.apiKey,
      sessionId: config.sessionId || this.generateSessionId(),
      autoObserve: config.autoObserve ?? true,
      observeInterval: config.observeInterval ?? 1000,
      allowedDomains: config.allowedDomains ?? [],
      debug: config.debug ?? false,
      highlightColor: config.highlightColor ?? '#FFD700',
      reconnect: config.reconnect ?? true,
      reconnectInterval: config.reconnectInterval ?? 3000
    };

    // Initialize event bus and logger
    this.eventBus = new EventBus();
    this.logger = new Logger(
      this.config.debug,
      this.config.debug ? LogLevel.DEBUG : LogLevel.INFO
    );

    this.logger.debug('SDK constructor called', { config });

    // Initialize WebSocket client
    this.wsClient = new WebSocketClient(
      this.config.mcpServerUrl,
      this.config.apiKey,
      this.config.sessionId,
      this.eventBus,
      this.logger,
      this.config.reconnect
    );

    // Initialize DOM observer
    this.domObserver = new DOMObserver(
      this.eventBus,
      this.logger,
      this.config.autoObserve,
      this.config.observeInterval
    );

    // Initialize action executor
    this.actionExecutor = new ActionExecutor(this.eventBus, this.logger);

    // Initialize UI highlighter
    this.uiHighlighter = new UIHighlighter(
      this.eventBus,
      this.logger,
      this.config.highlightColor
    );

    // Initialize security manager
    this.securityManager = new SecurityManager(
      this.eventBus,
      this.logger,
      this.config.allowedDomains
    );

    // Track observations
    this.eventBus.on('observation:complete', () => {
      this.status.lastObservation = Date.now();
    });

    // Track actions
    this.eventBus.on('action:success', () => {
      this.status.actionsExecuted++;
    });

    this.eventBus.on('action:failed', () => {
      this.status.errors++;
    });

    // Initialize status
    this.status = {
      initialized: false,
      connected: false,
      sessionId: this.config.sessionId,
      lastObservation: 0,
      actionsExecuted: 0,
      errors: 0
    };
  }

  async init(): Promise<void> {
    this.logger.info('Initializing SDK...');

    try {
      // Start DOM observer
      this.domObserver.start();

      this.status.initialized = true;
      this.eventBus.emit('initialized', { sessionId: this.status.sessionId });
      this.logger.info('SDK initialized successfully');
    } catch (error) {
      this.logger.error('Failed to initialize SDK', error);
      this.eventBus.emit('error', error);
      throw error;
    }
  }

  // Public event methods
  on(event: string, handler: EventHandler): void {
    this.eventBus.on(event, handler);
  }

  off(event: string, handler: EventHandler): void {
    this.eventBus.off(event, handler);
  }

  once(event: string, handler: EventHandler): void {
    this.eventBus.once(event, handler);
  }

  async connect(): Promise<void> {
    this.logger.info('Connecting to MCP server...');

    try {
      // 1. Connect WebSocket
      await this.wsClient.connect();
      this.status.connected = true;

      // 2. Wire DOM observations to WebSocket
      if (this.config.autoObserve) {
        this.eventBus.on('observation:complete', (data: unknown) => {
          if (this.isConnected()) {
            const observation = data as PageObservation;
            this.wsClient.send({
              type: 'observation',
              timestamp: Date.now(),
              payload: observation
            });
            this.logger.debug('Observation sent to MCP server', {
              elementsCount: observation.elements.length
            });
          }
        });
      }

      // 3. Listen for action commands from MCP server
      this.eventBus.on('ws:message', async (message: any) => {
        this.logger.debug('Received message from MCP server', { type: message.type });

        if (message.type === 'action' || message.type === 'executeAction') {
          try {
            // Execute the action
            const result = await this.executeAction(message.payload);

            // Send result back to MCP server
            this.wsClient.send({
              type: 'action-result',
              timestamp: Date.now(),
              payload: {
                messageId: message.messageId || message.id,
                success: result.success,
                error: result.error,
                result: result  // Send complete ActionResult object
              }
            });

            this.logger.debug('Action result sent to MCP server', {
              success: result.success
            });
          } catch (error) {
            // Send error back to MCP server
            this.wsClient.send({
              type: 'action-result',
              timestamp: Date.now(),
              payload: {
                messageId: message.messageId || message.id,
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error'
              }
            });

            this.logger.error('Action execution failed', error);
          }
        }
      });

      this.eventBus.emit('connected');
      this.logger.info('SDK fully connected and operational');
    } catch (error) {
      this.logger.error('Failed to connect', error);
      this.eventBus.emit('error', error);
      throw error;
    }
  }

  async disconnect(): Promise<void> {
    this.logger.info('Disconnecting from MCP server...');
    this.wsClient.disconnect();
    this.status.connected = false;
    this.eventBus.emit('disconnected');
    this.logger.info('Disconnected from MCP server');
  }

  isConnected(): boolean {
    return this.wsClient.isConnected();
  }

  observe(): PageObservation {
    return this.domObserver.observe();
  }

  async executeAction(action: Action): Promise<ActionResult> {
    // Validate action security
    const securityValidation = this.securityManager.validateAction(action);
    if (!securityValidation.allowed) {
      this.logger.warn('Action blocked by security', { action, reason: securityValidation.reason });
      return {
        success: false,
        action,
        error: `Security: ${securityValidation.reason}`,
        timestamp: Date.now(),
        duration: 0
      };
    }

    // Use sanitized action if available
    const safeAction = securityValidation.sanitized || action;
    return this.actionExecutor.execute(safeAction);
  }

  async executeActions(actions: Action[]): Promise<ActionResult[]> {
    return this.actionExecutor.executeSequence(actions);
  }

  getStatus(): SDKStatus {
    return { ...this.status };
  }

  isInitialized(): boolean {
    return this.status.initialized;
  }

  // Highlighter methods
  highlight(element: HTMLElement | string, options?: HighlightOptions): string {
    return this.uiHighlighter.highlight(element, options);
  }

  removeHighlight(id: string): boolean {
    return this.uiHighlighter.removeHighlight(id);
  }

  clearHighlights(): void {
    this.uiHighlighter.clearAll();
  }

  getActiveHighlights(): HighlightInfo[] {
    return this.uiHighlighter.getActiveHighlights();
  }

  // Security methods
  getSecurityViolations(): SecurityViolation[] {
    return this.securityManager.getViolations();
  }

  clearSecurityViolations(): void {
    this.securityManager.clearViolations();
  }

  getSecurityConfig(): SecurityConfig {
    return this.securityManager.getConfig();
  }

  updateSecurityConfig(config: Partial<SecurityConfig>): void {
    this.securityManager.updateConfig(config);
  }

  private validateConfig(config: NavigatorConfig): void {
    // Validate mcpServerUrl
    if (!config.mcpServerUrl) {
      throw new ConfigError('mcpServerUrl is required');
    }

    if (!config.mcpServerUrl.startsWith('ws://') && !config.mcpServerUrl.startsWith('wss://')) {
      throw new ConfigError('mcpServerUrl must be a valid WebSocket URL (ws:// or wss://)');
    }

    // Validate apiKey
    if (!config.apiKey) {
      throw new ConfigError('apiKey is required');
    }

    if (typeof config.apiKey !== 'string' || config.apiKey.trim().length === 0) {
      throw new ConfigError('apiKey must be a non-empty string');
    }
  }

  private generateSessionId(): string {
    // Use crypto.randomUUID if available, otherwise timestamp-based
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}
